import { showMessageError } from '@/shared/lib';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import { useEffect } from 'react';
import { t } from 'ttag';
import { generatePatientInfoData, type PatientInfoSection } from '../../lib/patientInfoDrawer';
import fetchStudyPatientInfo from '../../api/services/fetchStudyPatientInfo';
import fetchDeletedStudyPatientInfo from '../../api/services/fetchDeletedStudyPatientInfo';
import type { PatientInfoDrawerProps } from './patientInfoDrawer.types';

/**
 * Supplies the study patient info drawer with its formatted sections, loading flag and scroll
 * state. Fetches the active or deleted study's patient info whenever the drawer opens with a study
 * id, scrolls the drawer body back to the top on reopen, and exposes a scroll handler.
 *
 * @param studyId - Study whose patient info is shown.
 * @param visible - Whether the drawer is open.
 * @param isDeletedStudy - Whether the study is deleted, switching to the deleted-study query.
 * @param handleClickCloseButton - Closes the drawer when the study no longer exists.
 * @returns An object carrying the drawer state and the scroll handler.
 */
export const usePatientInfoDrawer = ({
  studyId,
  visible,
  isDeletedStudy,
  handleClickCloseButton,
}: Pick<
  PatientInfoDrawerProps,
  'studyId' | 'visible' | 'isDeletedStudy' | 'handleClickCloseButton'
>) => {
  const [state, setState] = useMergeState({
    isScroll: false,
    data: [] as (PatientInfoSection | undefined)[],
    isLoading: false,
  });

  /**
   * Loads the patient information shown in the study drawer. Queries the deleted-study or the active-study patient info depending on isDeletedStudy and formats the result for the drawer; on error it logs, clears loading and closes the drawer with a study-was-deleted message when the study is not found, otherwise showing the error.
   *
   * @returns Resolves after the patient data or the error handling completed.
   */
  const fetchData = async () => {
    try {
      setState({ isLoading: true });
      if (isDeletedStudy) {
        const result = await fetchDeletedStudyPatientInfo(studyId);
        const data = generatePatientInfoData(result.data.deletedStudy);
        setState({ data, isLoading: false });
      } else {
        const result = await fetchStudyPatientInfo(studyId);
        const data = generatePatientInfoData(result.data.study);
        setState({ data, isLoading: false });
      }
    } catch (error: any) {
      console.error(
        `Failed to fetch ${isDeletedStudy ? 'deleted study' : 'study'} patient info: `,
        error,
      );
      setState({ isLoading: false });
      if (error.message === 'Error: Study not found') {
        showMessageError({ content: t`Study was deleted` });
        handleClickCloseButton();
        return;
      }
      showMessageError({ content: error.message });
    }
  };

  /**
   * Tracks whether the patient info drawer body is scrolled so its header can show a divider. Flips the isScroll flag when the scroll target leaves or returns to the top.
   *
   * @param e - Scroll event from the drawer body.
   * @returns Nothing.
   */
  const handleScroll = (e: any) => {
    const { scrollTop } = e.target;
    if (scrollTop === 0 && state.isScroll) {
      setState({ isScroll: false });
    } else if (scrollTop !== 0 && !state.isScroll) {
      setState({ isScroll: true });
    }
  };

  useEffect(() => {
    if (visible && studyId) {
      fetchData();
    }
  }, [studyId, visible]);

  useUpdateEffect(() => {
    if (visible) {
      const myDiv = document.getElementById('drawer-body-container');
      if (myDiv) {
        myDiv.scrollTo({ top: 0 });
      }
    }
  }, [visible]);

  return { state, handleScroll };
};
